import { NavLink, Outlet } from 'react-router-dom';
import { Inbox, LayoutGrid, LogOut, ListOrdered, TicketPercent, SlidersHorizontal } from 'lucide-react';
import { getStorePlatformLabel, isPlatformManagedStore, useConsoleStore, useActiveStore } from '../store';
import { apiFetch } from '../lib/api';
import { ReachLogo } from './Logo';
import { cn } from '../lib/utils';

const NAV = [
  { to: '/', icon: LayoutGrid, label: 'Stores', end: true },
  { to: '/workspace', icon: SlidersHorizontal, label: 'Workspace' },
];

export function Layout() {
  const { user, logout } = useConsoleStore();
  const activeStore = useActiveStore();
  const isInternal = user?.role === 'support' || user?.role === 'staff' || user?.role === 'superadmin';
  const isStaffPlus = user?.role === 'staff' || user?.role === 'superadmin';
  const activeStorePlatformLabel = getStorePlatformLabel(activeStore);
  const activeStoreMode = isPlatformManagedStore(activeStore) ? 'Platform managed' : 'Console workspace';
  const navItems = isInternal
    ? [
        ...NAV,
        { to: '/internal', icon: LayoutGrid, label: 'Internal', end: false },
        { to: '/beta-requests', icon: Inbox, label: 'Beta Requests', end: false },
        { to: '/action-log', icon: ListOrdered, label: 'Action Log', end: false },
        ...(isStaffPlus ? [{ to: '/promo-codes', icon: TicketPercent, label: 'Promo Codes', end: false }] : []),
      ]
    : NAV;

  const handleLogout = async () => {
    await apiFetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    logout();
    window.location.href = '/login';
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <aside className="console-sidebar w-56 flex-shrink-0 flex flex-col">
        {/* Logo */}
        <div className="h-24 flex items-center gap-5 px-6 py-8 border-b border-white/5">
          <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '14px', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ReachLogo size="medium" showText={false} color="var(--text-inverse)" detailColor="var(--brand-secondary)" />
          </div>
          <span style={{ fontSize: '21px', fontWeight: 800, color: 'var(--text-inverse)', letterSpacing: '0.06em', fontFamily: "'Inter', sans-serif", lineHeight: 1, flexShrink: 0 }}>
            REACH<span style={{ color: 'var(--brand-secondary)', fontSize: '26px', lineHeight: 0 }}>.</span>
          </span>
        </div>

        {/* Active store pill */}
        {activeStore && (
          <div className="mx-3 mt-3 px-3 py-2.5 rounded-xl bg-white/5 border border-white/5">
            <p className="text-xs font-semibold text-white truncate">{activeStore.externalDomain}</p>
            <p className="text-[11px] text-zinc-400 mt-0.5">{activeStore.planTier} plan · {activeStorePlatformLabel}</p>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-200/80">{activeStoreMode}</p>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                isActive
                  ? 'bg-white/10 text-white font-bold'
                  : 'text-zinc-400 hover:bg-white/5 hover:text-white'
              )}
            >
              <Icon size={15} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User + logout */}
        <div className="p-3 border-t border-white/5">
          <p className="text-[11px] text-zinc-400 truncate px-1 mb-2 font-medium opacity-60">{user?.email}</p>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-zinc-400 hover:bg-white/5 hover:text-red-400 transition-colors"
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      </aside>

      <main className="console-main flex-1 overflow-y-auto relative">
        <div className="absolute inset-0 bg-grid-zinc-200/50 [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />
        <div className="relative">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
